import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, MapPin, Info, Globe, Image, RefreshCw, Save, CheckCircle, XCircle, Clock, AlertCircle, Phone, Edit2, ChevronLeft } from "lucide-react";
import api from "../services/api";
import { toast } from "react-toastify";
import LoadingSpinner from "../components/LoadingSpinner";
import { getUserInfoFromToken } from "../services/auth";

const CATEGORIES = [
  "AUTOMOTIVE", "BEAUTY_SPA_AND_SALON", "CLOTHING_AND_APPAREL",
  "EDUCATION", "ENTERTAINMENT", "EVENT_PLANNING_AND_SERVICE",
  "FINANCE_AND_BANKING", "FOOD_AND_GROCERY", "PUBLIC_SERVICE",
  "HOTEL_AND_LODGING", "MEDICAL_AND_HEALTH", "NON_PROFIT",
  "PROFESSIONAL_SERVICES", "RESTAURANT", "SHOPPING_AND_RETAIL",
  "TRAVEL_AND_TRANSPORTATION", "OTHER",
];

const NAME_STATUS_UI = {
  PENDING_REVIEW:           { label: "Processing — pending Meta review", icon: Clock,        color: "text-yellow-600 bg-yellow-50 border-yellow-200" },
  APPROVED:                 { label: "Approved",                         icon: CheckCircle,  color: "text-green-600 bg-green-50 border-green-200"   },
  AVAILABLE_WITHOUT_REVIEW: { label: "Approved",                         icon: CheckCircle,  color: "text-green-600 bg-green-50 border-green-200"   },
  DECLINED:                 { label: "Rejected — check formatting/symbols", icon: XCircle,  color: "text-red-600 bg-red-50 border-red-200"         },
  EXPIRED:                  { label: "Expired",                          icon: AlertCircle,  color: "text-gray-600 bg-gray-50 border-gray-200"      },
};

const NameStatusBadge = ({ status }) => {
  if (!status) return null;
  const ui = NAME_STATUS_UI[status] || { label: status, icon: Info, color: "text-gray-600 bg-gray-50 border-gray-200" };
  const Icon = ui.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${ui.color}`}>
      <Icon className="w-3.5 h-3.5" />
      {ui.label}
    </span>
  );
};

const BusinessProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [uploadingPic, setUploadingPic] = useState(false);
  const [showNameForm, setShowNameForm] = useState(false);
  const [nameForm, setNameForm] = useState({ name: "", pin: "" });
  const [submittingName, setSubmittingName] = useState(false);
  const [showPinForm, setShowPinForm] = useState(false);
  const [pinForm, setPinForm] = useState({ currentPin: "", newPin: "", confirmPin: "" });
  const [submittingPin, setSubmittingPin] = useState(false);
  const [disablingPin, setDisablingPin] = useState(false);
  const fileRef = useRef(null);

  const [profile, setProfile] = useState({
    description: "", address: "", about: "", vertical: "", websites: ["", ""],
    profile_picture_url: null, displayName: null, nameStatus: null,
  });

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const r = await api.get("/business-profile");
      const d = r.data.data;
      setProfile({
        description: d.description || "",
        address: d.address || "",
        about: d.about || "",
        vertical: d.vertical || "",
        websites: [d.websites?.[0] || "", d.websites?.[1] || ""],
        profile_picture_url: d.profile_picture_url || null,
        displayName: d.displayName || null,
        nameStatus: d.nameStatus || null,
      });
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfile(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const websites = profile.websites.filter((w) => w.trim());
      await api.post("/business-profile", {
        description: profile.description,
        address: profile.address,
        about: profile.about,
        vertical: profile.vertical || undefined,
        websites: websites.length ? websites : undefined,
      });
      toast.success("Profile updated successfully");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleCheckStatus = async () => {
    setCheckingStatus(true);
    try {
      const r = await api.get("/business-profile/name-status");
      setProfile((prev) => ({ ...prev, nameStatus: r.data.data.nameStatus, displayName: r.data.data.displayName }));
      toast.info(`Name status: ${r.data.data.nameStatus || "Unknown"}`);
    } catch (err) {
      toast.error("Failed to fetch name status");
    } finally {
      setCheckingStatus(false);
    }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
      return toast.error("Only JPG/PNG images are allowed");
    }
    setUploadingPic(true);
    try {
      const form = new FormData();
      form.append("picture", file);
      await api.post("/business-profile/picture", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profile picture updated");
      fetchProfile();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to upload picture");
    } finally {
      setUploadingPic(false);
      e.target.value = "";
    }
  };

  const waPhone = getUserInfoFromToken()?.waPhone || null;

  const handleDisplayNameSubmit = async () => {
    if (!nameForm.name.trim()) return toast.error("Display name is required");
    if (!nameForm.pin.trim()) return toast.error("PIN is required");
    setSubmittingName(true);
    try {
      await api.post("/business-profile/display-name", { name: nameForm.name.trim(), pin: nameForm.pin.trim() });
      toast.success("Display name change submitted — status is now Pending Review");
      setProfile((prev) => ({ ...prev, nameStatus: "PENDING_REVIEW" }));
      setNameForm({ name: "", pin: "" });
      setShowNameForm(false);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to update display name");
    } finally {
      setSubmittingName(false);
    }
  };

  const handleDisablePin = async () => {
    if (!window.confirm("Disable two-step verification? This only works after Meta's 7-day cooldown.")) return;
    setDisablingPin(true);
    try {
      await api.delete("/business-profile/two-step-verification");
      toast.success("Two-step verification disabled");
      setShowPinForm(false);
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed to disable PIN";
      const isCooldown = msg.toLowerCase().includes("7") || msg.toLowerCase().includes("cooldown") || msg.toLowerCase().includes("wait");
      toast.error(isCooldown ? "Cannot disable yet — Meta requires a 7-day waiting period after the PIN was last set." : msg);
    } finally {
      setDisablingPin(false);
    }
  };

  const handlePinSubmit = async () => {
    if (!/^\d{6}$/.test(pinForm.newPin)) return toast.error("PIN must be exactly 6 digits");
    if (pinForm.newPin !== pinForm.confirmPin) return toast.error("PINs do not match");
    setSubmittingPin(true);
    try {
      await api.post("/business-profile/two-step-verification", {
        pin: pinForm.newPin,
        currentPin: pinForm.currentPin || undefined,
      });
      toast.success("Two-step verification PIN updated successfully");
      setPinForm({ currentPin: "", newPin: "", confirmPin: "" });
      setShowPinForm(false);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to update PIN");
    } finally {
      setSubmittingPin(false);
    }
  };

  const set = (field, value) => setProfile((prev) => ({ ...prev, [field]: value }));
  const setWebsite = (i, value) => setProfile((prev) => {
    const w = [...prev.websites];
    w[i] = value;
    return { ...prev, websites: w };
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="md" text="Loading profile..." />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
            title="Go back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Business Profile</h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage your WhatsApp Business profile visible to customers</p>
          </div>
        </div>
        <button
          onClick={fetchProfile}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Profile Picture */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <Image className="w-4 h-4 text-gray-400" /> Profile Picture
        </h2>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-full bg-gray-100 border border-gray-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
            {profile.profile_picture_url ? (
              <img src={profile.profile_picture_url} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <Building2 className="w-8 h-8 text-gray-300" />
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-2">Square JPG or PNG, ideally 640×640px, max 5MB</p>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={handlePictureUpload} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploadingPic}
              className="text-sm bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 rounded-lg disabled:opacity-50 transition-colors"
            >
              {uploadingPic ? "Uploading..." : "Change Picture"}
            </button>
          </div>
        </div>
      </div>

      {/* Display Name Status */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-gray-400" /> Display Name
            </h2>
            {profile.displayName && (
              <p className="text-base font-semibold text-gray-900 mt-1">{profile.displayName}</p>
            )}
            {waPhone && (
              <div className="flex items-center gap-1.5 mt-1">
                <Phone className="w-3.5 h-3.5 text-green-500" />
                <p className="text-sm text-green-600 font-medium">{waPhone}</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <NameStatusBadge status={profile.nameStatus} />
            <button
              onClick={handleCheckStatus}
              disabled={checkingStatus}
              className="text-xs text-blue-500 hover:text-blue-700 underline disabled:opacity-50"
            >
              {checkingStatus ? "Checking..." : "Refresh status"}
            </button>
          </div>
        </div>
        {profile.nameStatus === "DECLINED" && (
          <p className="text-xs text-red-600 mt-2 bg-red-50 rounded-lg px-3 py-2">
            Your display name was rejected by Meta. Common reasons: contains symbols, URLs, or violates naming policies. Update and resubmit below.
          </p>
        )}
        {profile.nameStatus === "PENDING_REVIEW" && (
          <p className="text-xs text-yellow-700 mt-2 bg-yellow-50 rounded-lg px-3 py-2">
            Display name is under review by Meta. This typically takes a few hours. Status is checked automatically every hour.
          </p>
        )}

        {/* Change display name */}
        <div className="mt-3 border-t border-gray-100 pt-3">
          <button
            onClick={() => setShowNameForm((v) => !v)}
            className="flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-700 font-medium transition-colors"
          >
            <Edit2 className="w-3.5 h-3.5" />
            {showNameForm ? "Cancel" : "Change Display Name"}
          </button>

          {showNameForm && (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-gray-500">
                Meta requires your two-step verification PIN to change the display name. The new name will be reviewed by Meta before going live.
              </p>
              <input
                type="text"
                value={nameForm.name}
                onChange={(e) => setNameForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="New display name"
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <input
                type="password"
                value={nameForm.pin}
                onChange={(e) => setNameForm((f) => ({ ...f, pin: e.target.value }))}
                placeholder="Two-step verification PIN (6 digits)"
                maxLength={6}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button
                onClick={handleDisplayNameSubmit}
                disabled={submittingName || !nameForm.name.trim() || !nameForm.pin.trim()}
                className="w-full text-sm bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 rounded-lg disabled:opacity-50 transition-colors"
              >
                {submittingName ? "Submitting..." : "Submit for Review"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Profile Details */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <Building2 className="w-4 h-4 text-gray-400" /> Profile Details
        </h2>
        <div className="space-y-4">

          {/* About */}
          <div>
            <div className="flex justify-between mb-1">
              <label className="text-xs font-medium text-gray-600 flex items-center gap-1">
                <Info className="w-3.5 h-3.5" /> About
              </label>
              <span className={`text-xs ${profile.about.length > 130 ? "text-orange-500" : "text-gray-400"}`}>
                {profile.about.length}/139
              </span>
            </div>
            <input
              type="text"
              value={profile.about}
              onChange={(e) => set("about", e.target.value)}
              maxLength={139}
              placeholder="A short description shown in your contact info"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Description */}
          <div>
            <div className="flex justify-between mb-1">
              <label className="text-xs font-medium text-gray-600">Description</label>
              <span className={`text-xs ${profile.description.length > 490 ? "text-orange-500" : "text-gray-400"}`}>
                {profile.description.length}/512
              </span>
            </div>
            <textarea
              value={profile.description}
              onChange={(e) => set("description", e.target.value)}
              maxLength={512}
              rows={3}
              placeholder="Describe your business"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Address */}
          <div>
            <div className="flex justify-between mb-1">
              <label className="text-xs font-medium text-gray-600 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" /> Address
              </label>
              <span className={`text-xs ${profile.address.length > 245 ? "text-orange-500" : "text-gray-400"}`}>
                {profile.address.length}/256
              </span>
            </div>
            <input
              type="text"
              value={profile.address}
              onChange={(e) => set("address", e.target.value)}
              maxLength={256}
              placeholder="Business address"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
            <select
              value={profile.vertical}
              onChange={(e) => set("vertical", e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            >
              <option value="">Select a category</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
              ))}
            </select>
          </div>

          {/* Websites */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
              <Globe className="w-3.5 h-3.5" /> Websites (max 2)
            </label>
            <div className="space-y-2">
              {[0, 1].map((i) => (
                <input
                  key={i}
                  type="url"
                  value={profile.websites[i]}
                  onChange={(e) => setWebsite(i, e.target.value)}
                  placeholder={`https://example.com${i === 1 ? " (optional)" : ""}`}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Save */}
      {/* Two-Step Verification */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-700">Two-Step Verification</h2>
            <p className="text-xs text-gray-500 mt-0.5">Set or change the 6-digit PIN for your WhatsApp Business account</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDisablePin}
              disabled={disablingPin}
              className="text-xs text-red-500 hover:text-red-700 font-medium underline disabled:opacity-50 transition-colors"
            >
              {disablingPin ? "Disabling..." : "Forgot / Disable"}
            </button>
            <button
              onClick={() => setShowPinForm((v) => !v)}
              className="text-xs text-blue-500 hover:text-blue-700 font-medium underline transition-colors"
            >
              {showPinForm ? "Cancel" : "Set PIN"}
            </button>
          </div>
        </div>

        {showPinForm && (
          <div className="mt-4 space-y-3">
            <p className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
              If you already have a PIN set, enter it as the current PIN. If setting for the first time, leave current PIN blank.
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Current PIN <span className="text-gray-400">(leave blank if not set)</span></label>
              <input
                type="password"
                value={pinForm.currentPin}
                onChange={(e) => setPinForm((f) => ({ ...f, currentPin: e.target.value }))}
                placeholder="Current 6-digit PIN"
                maxLength={6}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">New PIN</label>
              <input
                type="password"
                value={pinForm.newPin}
                onChange={(e) => setPinForm((f) => ({ ...f, newPin: e.target.value }))}
                placeholder="New 6-digit PIN"
                maxLength={6}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Confirm New PIN</label>
              <input
                type="password"
                value={pinForm.confirmPin}
                onChange={(e) => setPinForm((f) => ({ ...f, confirmPin: e.target.value }))}
                placeholder="Repeat new PIN"
                maxLength={6}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <button
              onClick={handlePinSubmit}
              disabled={submittingPin || !pinForm.newPin || !pinForm.confirmPin}
              className="w-full text-sm bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 rounded-lg disabled:opacity-50 transition-colors"
            >
              {submittingPin ? "Saving..." : "Save PIN"}
            </button>
          </div>
        )}
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-medium py-2.5 rounded-xl disabled:opacity-50 transition-colors"
      >
        <Save className="w-4 h-4" />
        {saving ? "Saving..." : "Save Profile"}
      </button>
    </div>
  );
};

export default BusinessProfile;
